<script setup lang="ts">
import type { IInvoiceSummaryRow } from '@/types/rc/invoices'
import { currencyFormatter } from '@/helpers/ep/formatters'
import type { TableColumnCtx } from 'element-plus'
import { h, type VNode } from 'vue'
import { formatCurrency } from '@/util/formatters'

defineProps<{ summary?: IInvoiceSummaryRow[] }>()

interface SummaryMethodProps<T extends Record<PropertyKey, any> = IInvoiceSummaryRow> {
  columns: TableColumnCtx<T>[]
  data: T[]
}

const getSummary = (summaryProps: SummaryMethodProps) => {
  const { columns, data } = summaryProps
  const sums: (string | VNode)[] = []

  columns.forEach((column, index) => {
    // First column: 'Total' label
    if (column.property === 'description') {
      sums[index] = h('span', { class: 'underline' }, ['Total'])
      return
    }

    // For all columns except quantity and total, set to empty or N/A
    if (!['quantity', 'total'].includes(column.property)) {
      sums[index] = ''
      return
    }

    // For those, calculate the sum
    const values = data.map((item) => Number(item[column.property as keyof IInvoiceSummaryRow]))
    if (!values.every((value) => Number.isNaN(value))) {
      const total = values.reduce((prev, curr) => {
        const value = Number(curr)
        if (!Number.isNaN(value)) {
          return prev + curr
        } else {
          return prev
        }
      }, 0)

      sums[index] = `${index === 3 ? formatCurrency(total) : total.toString()}`
    } else {
      sums[index] = 'N/A'
    }
  })

  return sums
}
</script>

<template>
  <el-table v-if="summary?.length" :data="summary" show-summary :summary-method="getSummary">
    <el-table-column label="Summary" header-align="center">
      <el-table-column prop="description" label="Description" />
      <el-table-column prop="quantity" label="Quantity" />
      <el-table-column prop="unit_cost" label="Unit Cost" :formatter="currencyFormatter" />
      <el-table-column prop="total" label="Total" :formatter="currencyFormatter" />
    </el-table-column>
  </el-table>
</template>
